import React from "react";
import { useParams, Link } from "react-router-dom";
import { Box, Typography, Grid, Button, CircularProgress, Divider, Chip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { getService } from "../api/services";
import PageShell from "../components/layout/PageShell";
import ContactSection from "../components/shared/ContactSection";

export default function ServiceDetailPage() {
    const { slug } = useParams();

    const { data: service, isLoading, isError } = useQuery({
        queryKey: ["service", slug],
        queryFn: () => getService(slug).then(r => r.data),
        enabled: !!slug,
    });

    if (isLoading) return (
        <PageShell>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        </PageShell>
    );

    if (isError || !service) return (
        <PageShell>
            <Box textAlign="center" py={20}>
                <Typography variant="h4" gutterBottom>Service not found</Typography>
                <Button component={Link} to="/business" startIcon={<ArrowLeft size={16} />}>
                    Back to Services
                </Button>
            </Box>
        </PageShell>
    );

    return (
        <PageShell>
            {/* Hero */}
            <Box sx={{
                background: "linear-gradient(135deg, #0A0F1E 0%, #0f2044 100%)",
                pt: 16, pb: 10, position: "relative", overflow: "hidden",
            }}>
                <Box className="tech-grid" sx={{ position: "absolute", inset: 0, opacity: 0.15, pointerEvents: "none" }} />
                <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
                    <Button
                        component={Link} to="/business"
                        startIcon={<ArrowLeft size={16} />}
                        sx={{ color: "rgba(255,255,255,0.6)", mb: 3, "&:hover": { color: "white" } }}
                    >
                        Back to Services
                    </Button>
                    <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 700, letterSpacing: "0.2em", display: "block", mb: 1 }}>
                        Business Service
                    </Typography>
                    <Typography variant="h2" fontWeight={700} color="white"
                        sx={{ fontSize: { xs: "1.8rem", md: "2.8rem" }, lineHeight: 1.15, mb: 2 }}>
                        {service.name}
                    </Typography>
                    <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.70)", fontWeight: 400, maxWidth: 600 }}>
                        {service.short_description}
                    </Typography>
                    {service.card_image_url && (
                        <Box component="img" src={service.card_image_url} alt={service.name}
                            sx={{ width: "100%", maxWidth: 480, borderRadius: 3, mt: 3 }} />
                    )}
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: 8 }}>
                <Grid container spacing={6}>
                    <Grid item xs={12} lg={8}>
                        {service.detailed_description && (
                            <Box sx={{ mb: 5 }}>
                                <Typography variant="h5" fontWeight={700} gutterBottom>Overview</Typography>
                                <Divider sx={{ mb: 2.5 }} />
                                <Typography variant="body1" color="text.secondary" lineHeight={1.9}>
                                    {service.detailed_description}
                                </Typography>
                            </Box>
                        )}


                    </Grid>

                    {/* Sidebar CTA */}
                    <Grid item xs={12} lg={4}>
                        <Box sx={{ position: "sticky", top: 100, bgcolor: "#F8FAFC", borderRadius: 3, p: 3.5 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom>Interested in this service?</Typography>
                            <Typography variant="body2" color="text.secondary" mb={2.5} lineHeight={1.7}>
                                Our team will reach out with a custom proposal tailored to your organization's needs.
                            </Typography>
                            <Button variant="contained" fullWidth size="large" component="a" href="#contact" sx={{ borderRadius: 2, mb: 1.5 }}>
                                Request Consultation
                            </Button>
                            <Button variant="outlined" fullWidth size="large" component="a" href="#contact" sx={{ borderRadius: 2 }}>
                                Get a Proposal
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <ContactSection />
        </PageShell>
    );
}